/**
 * Hook: useTOS
 *
 * Public hook for TOS-specific operations.
 * Convenience wrapper around useSettings for TOS functionality.
 *
 * @example
 * ```tsx
 * const { tosAcceptance, acceptTOS, hasAccepted, needsAcceptance } = useTOS();
 *
 * // Check if user has accepted TOS (and it hasn't been updated since)
 * if (needsAcceptance()) {
 *   await acceptTOS();
 * }
 * ```
 */

import { useCallback, useMemo } from 'react';
import { useSettings } from './useSettings';

// A guest can't be uniquely identified, so their acceptance can't be persisted
// server-side. It lives per-tab in sessionStorage: a rejoin within the same
// session isn't re-prompted, a fresh session always is.
const GUEST_TOS_ACCEPTED_KEY = 'sangoma-meet-guest-tos-accepted';

export const useTOS = () => {
  const {
    tosAcceptance,
    tosMetadata,
    isLoadingTOS,
    tosError,
    acceptTOS: acceptTosMutation,
    clearTOS: clearTosMutation,
    refetchTOS,
  } = useSettings();

  /**
   * Check if user has accepted TOS at all
   */
  const hasAccepted = useCallback((): boolean => {
    if (!tosAcceptance?.accepted) {
      return false;
    }
    return true;
  }, [tosAcceptance]);

  /**
   * Check if TOS needs to be accepted (or re-accepted)
   * Returns true if:
   * 1. User has never accepted TOS, OR
   * 2. TOS has been modified after user's acceptance
   */
  const needsAcceptance = useCallback((): boolean => {
    // If no acceptance record, needs acceptance
    if (!tosAcceptance?.accepted) {
      return true;
    }

    // If we have metadata about TOS modification date
    if (tosMetadata?.modified) {
      const acceptedDate = new Date(tosAcceptance.accepted);
      const modifiedDate = new Date(tosMetadata.modified);

      // If TOS was modified after acceptance, needs re-acceptance
      if (modifiedDate > acceptedDate) {
        console.log('[useTOS] TOS needs re-acceptance:', {
          acceptedDate: acceptedDate.toISOString(),
          modifiedDate: modifiedDate.toISOString(),
        });
        return true;
      }
    }

    // Otherwise, user's acceptance is still valid
    return false;
  }, [tosAcceptance, tosMetadata]);

  // Guest equivalents of needsAcceptance/acceptTOS, backed by sessionStorage
  // instead of the server.
  const guestNeedsAcceptance = useCallback((): boolean => {
    try {
      return sessionStorage.getItem(GUEST_TOS_ACCEPTED_KEY) !== '1';
    } catch {
      return true;
    }
  }, []);

  const acceptGuestTos = useCallback((): void => {
    try {
      sessionStorage.setItem(GUEST_TOS_ACCEPTED_KEY, '1');
    } catch {
      // sessionStorage unavailable (e.g. private mode) — re-prompted next join
    }
  }, []);

  /**
   * Get acceptance date as Date object
   */
  const acceptanceDate = useMemo(() => {
    if (!tosAcceptance?.accepted) {
      return null;
    }
    return new Date(tosAcceptance.accepted);
  }, [tosAcceptance]);

  /**
   * Get TOS last modified date as Date object
   */
  const tosModifiedDate = useMemo(() => {
    if (!tosMetadata?.modified) {
      return null;
    }
    return new Date(tosMetadata.modified);
  }, [tosMetadata]);

  return {
    // Data
    tosAcceptance,
    tosMetadata,
    isLoadingTOS,
    tosError,
    acceptanceDate,
    tosModifiedDate,

    // Actions
    acceptTOS: acceptTosMutation,
    clearTOS: clearTosMutation,
    refetchTOS,

    // Utilities
    hasAccepted,
    needsAcceptance,
    guestNeedsAcceptance,
    acceptGuestTos,
  };
};
